const DEFAULTS = {
  stringLimit: 300,
  stackLimit: 2000,
  logLineLimit: 2048,
  arraySample: 3,
  objectKeySample: 10,
};

const normalizeBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (value === undefined || value === null) return false;
  const str = String(value).trim().toLowerCase();
  return str === "1" || str === "true" || str === "yes" || str === "on";
};

const resolveDebugFlag = () => {
  if (typeof process !== "undefined" && process?.env) {
    return process.env.DEBUG_LOGS;
  }
  return undefined;
};

const DEBUG_LOGS = normalizeBoolean(resolveDebugFlag());

const LEVEL_PRIORITY = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

const normalizeLogLevel = (value) => {
  if (!value || typeof value !== "string") return null;
  const level = value.trim().toLowerCase();
  if (!(level in LEVEL_PRIORITY)) return null;
  return level;
};

const resolveLogLevel = () => {
  if (typeof process !== "undefined" && process?.env) {
    return process.env.REACT_APP_LOG_LEVEL || process.env.LOG_LEVEL;
  }
  return undefined;
};

const DEFAULT_LOG_LEVEL = DEBUG_LOGS ? "debug" : "warn";
const MIN_LOG_LEVEL = normalizeLogLevel(resolveLogLevel()) || DEFAULT_LOG_LEVEL;

const shouldLog = (level) =>
  LEVEL_PRIORITY[level] >= LEVEL_PRIORITY[MIN_LOG_LEVEL];

export const truncateString = (value, max = DEFAULTS.stringLimit) => {
  if (value === undefined || value === null) return value;
  const str = typeof value === "string" ? value : String(value);
  if (str.length <= max) return str;
  return `${str.slice(0, Math.max(0, max - 12))}...truncated`;
};

export const summarizeError = (error) => {
  if (!error) return { errorName: "Error", errorMessage: "Unknown error" };
  const err = error instanceof Error ? error : new Error(String(error));
  return {
    errorName: err.name || "Error",
    errorMessage: truncateString(err.message),
    stack: err.stack
      ? truncateString(err.stack, DEFAULTS.stackLimit)
      : undefined,
  };
};

export const summarizeArray = (arr, sampleSize = DEFAULTS.arraySample) => {
  if (!Array.isArray(arr)) return { type: typeof arr };
  return {
    type: "array",
    count: arr.length,
    sample: arr.slice(0, sampleSize).map((item) => {
      if (item && typeof item === "object") {
        return item.id || item._id || item.leadId || item.clientId || "object";
      }
      return item;
    }),
  };
};

export const summarizeObject = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return { type: typeof obj };
  }
  const keys = Object.keys(obj);
  const summary = {
    type: obj.constructor?.name || "object",
    key_count: keys.length,
    keys_sample: keys.slice(0, DEFAULTS.objectKeySample),
  };
  if (obj.id) summary.id = truncateString(obj.id, 64);
  if (obj._id) summary._id = truncateString(obj._id, 64);
  return summary;
};

const normalizeFields = (fields) => {
  if (!fields || typeof fields !== "object") return {};
  const out = {};
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined) continue;
    if (typeof value === "string") out[key] = truncateString(value);
    else if (value instanceof Error) out[key] = summarizeError(value);
    else if (Array.isArray(value)) out[key] = summarizeArray(value);
    else if (typeof value === "object") out[key] = summarizeObject(value);
    else out[key] = value;
  }
  return out;
};

const limitLogLine = (record) => {
  let line = JSON.stringify(record);
  if (line.length <= DEFAULTS.logLineLimit) return line;
  const minimal = {
    ts: record.ts,
    level: record.level,
    scope: record.scope,
    msg: truncateString(record.msg, 120),
    truncated: true,
  };
  line = JSON.stringify(minimal);
  if (line.length <= DEFAULTS.logLineLimit) return line;
  return `${line.slice(0, DEFAULTS.logLineLimit - 16)}...truncated`;
};

const write = (level, scope, msg, fields) => {
  const record = {
    ts: new Date().toISOString(),
    level,
    scope,
    msg: truncateString(msg ?? ""),
    ...normalizeFields(fields),
  };
  const line = limitLogLine(record);
  if (level === "error") console.error(line);
  else if (level === "warn") console.warn(line);
  else console.log(line);
};

export const createLogger = (scope) => ({
  debug: (msg, fields) => {
    if (!shouldLog("debug")) return;
    write("debug", scope, msg, fields);
  },
  info: (msg, fields) => {
    if (!shouldLog("info")) return;
    write("info", scope, msg, fields);
  },
  warn: (msg, fields) => {
    if (!shouldLog("warn")) return;
    write("warn", scope, msg, fields);
  },
  error: (msg, fields) => {
    if (!shouldLog("error")) return;
    write("error", scope, msg, fields);
  },
});
